import { useState } from 'react';
import { trackService } from 'data/services/trackService';
import { cookies } from 'sharedKernel/cookies/behavior';
import { API_BASE_URL } from 'sharedKernel/constants';

export interface VideoUploadState {
  isUploading: boolean;
  uploadProgress: number;
  videoStatus: any;
  error: string | null;
  videoId: string | null;
}

export const useVideoUpload = () => {
  const [state, setState] = useState<VideoUploadState>({
    isUploading: false,
    uploadProgress: 0,
    videoStatus: null,
    error: null,
    videoId: null,
  });

  const getToken = () => {
    return localStorage.getItem('jwt_token') || 
           sessionStorage.getItem('jwt') || 
           cookies.get('_cred')?.data?.token;
  };

  const getVideoDurationMinutes = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      const url = URL.createObjectURL(file);
      video.src = url;
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        const seconds = video.duration || 0;
        const minutes = Math.max(1, Math.ceil(seconds / 60));
        resolve(minutes);
      };
      video.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(1);
      };
    });
  };

  const uploadVideo = async (lessonId: string, file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('video', file);
    const minutes = await getVideoDurationMinutes(file);
    formData.append('minute', String(minutes));

    try {
      setState(prev => ({ 
        ...prev, 
        isUploading: true, 
        uploadProgress: 0, 
        error: null 
      }));

      const token = getToken();
      if (!token) {
        throw new Error('Token de autorização não encontrado');
      }

      const response = await fetch(`${API_BASE_URL}v1/lesson/upload-video/${lessonId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erro na resposta:', errorText);
        throw new Error(`Erro no upload: ${response.status} ${response.statusText} - ${errorText}`);
      }

      let returnedId: string | null = null;
      try {
        const result = await response.json();
        returnedId = result?.videoId || result?.id || null;
      } catch {}
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        videoId: returnedId 
      }));

      return returnedId || '';
    } catch (error: any) {
      console.error('❌ Erro no upload:', error);
      setState(prev => ({ 
        ...prev, 
        isUploading: false, 
        error: error.message 
      }));
      throw error;
    }
  };

  const checkVideoStatus = async (videoUid: string) => {
    try {
      setState(prev => ({ ...prev, error: null }));

      const token = getToken();
      if (!token) {
        throw new Error('Token de autorização não encontrado');
      }

      const response = await fetch(`${API_BASE_URL}lesson/video/status/${videoUid}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ao verificar status: ${response.status}`);
      }

      const result = await response.json();
      
      setState(prev => ({ ...prev, videoStatus: result }));
      return result;
    } catch (error: any) {
      setState(prev => ({ ...prev, error: error.message }));
      throw error;
    }
  };

  const resetState = () => {
    setState({
      isUploading: false,
      uploadProgress: 0,
      videoStatus: null,
      error: null,
      videoId: null,
    });
  };

  return {
    ...state,
    uploadVideo,
    checkVideoStatus,
    resetState,
  };
};
